import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';
import { HelmetProvider } from 'react-helmet-async';

import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeContext';
import { getTheme } from './theme/themeConfig';
import { suppressResizeObserverError } from './utils/resizeObserverErrorHandler';
import Navbar from './components/Navbar';
import { FooterSection } from './components/ui/footer-section';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import HealthTree from './pages/UnifiedTalentTree';
import HealthAssessment from './pages/HealthAssessment';
import HealthWorth from './pages/HealthWorth';
import Goals from './pages/Goals';
import Analytics from './pages/Analytics';
import Profile from './pages/Profile';
import Pricing from './pages/Pricing';
import Login from './pages/Login';
import Register from './pages/Register';
import EmailConfirmation from './pages/EmailConfirmation';
import EmailConfirmed from './pages/EmailConfirmed';
import OAuthCallback from './pages/OAuthCallback';
import SubscriptionManagement from './components/SubscriptionManagement';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import SubscriptionReturn from './components/SubscriptionReturn';
import ProtectedRoute from './components/ProtectedRoute';
import WeeklyLockInPage from './pages/WeeklyLockInPage';
import ActivityLog from './pages/ActivityLog';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ContactUs from './pages/ContactUs';
import GoogleOneTapWrapper from './components/GoogleOneTapWrapper';
import ErrorBoundary from './components/ErrorBoundary';

const queryClient = new QueryClient();

// App content component that uses theme context
const AppContent: React.FC = () => {
  const { mode } = useThemeMode();
  const theme = getTheme(mode);

  useEffect(() => {
    // Suppress benign ResizeObserver errors
    suppressResizeObserverError();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <HelmetProvider>
        <ErrorBoundary>
          <AuthProvider>
            <ErrorBoundary>
              <SubscriptionProvider>
                <Router>
                    <div className="App" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
                      <ErrorBoundary>
                        <Navbar />
                      </ErrorBoundary>
                      <Box component="main" sx={{ flex: 1, paddingTop: { xs: '64px', sm: '64px' }, backgroundColor: 'background.default' }}>
                        <ErrorBoundary>
                          <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/email-confirmation" element={<EmailConfirmation />} />
                  <Route path="/email-confirmed" element={<EmailConfirmed />} />
                  <Route path="/oauth-callback" element={<OAuthCallback />} />
                  <Route path="/test-success" element={<SubscriptionSuccess />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/talent-tree"
                    element={
                      <ProtectedRoute>
                        <HealthTree />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/health-assessment"
                    element={
                      <ProtectedRoute>
                        <HealthAssessment />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/health-worth"
                    element={
                      <ProtectedRoute>
                        <HealthWorth />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/goals"
                    element={
                      <ProtectedRoute>
                        <Goals />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/analytics"
                    element={
                      <ProtectedRoute>
                        <Analytics />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/weekly-lockin"
                    element={
                      <ProtectedRoute>
                        <WeeklyLockInPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/activity-log"
                    element={
                      <ProtectedRoute>
                        <ActivityLog />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/subscription"
                    element={
                      <ProtectedRoute>
                        <SubscriptionManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/subscription/success"
                    element={<SubscriptionSuccess />}
                  />
                  <Route
                    path="/subscription/return"
                    element={<SubscriptionReturn />}
                  />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/contact" element={<ContactUs />} />
                          </Routes>
                        </ErrorBoundary>
                      </Box>
                      <ErrorBoundary>
                        <FooterSection />
                      </ErrorBoundary>
                      <Toaster
                  position="bottom-right"
                  toastOptions={{
                    duration: 200,
                    style: {
                      background: mode === 'dark' ? '#1a1a2e' : '#ffffff',
                      color: mode === 'dark' ? '#fff' : '#1a202c',
                      border: mode === 'dark' ? '1px solid rgba(255, 215, 0, 0.5)' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '14px',
                      fontWeight: '500',
                      boxShadow: mode === 'dark' 
                        ? '0 6px 15px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 215, 0, 0.3)'
                        : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      zIndex: 9999,
                      minWidth: '180px',
                      textAlign: 'left',
                    },
                    success: {
                      style: {
                        background: mode === 'dark' ? '#2e7d32' : '#059669',
                        border: mode === 'dark' ? '1px solid rgba(76, 175, 80, 0.7)' : '1px solid #10b981',
                        color: '#ffffff',
                      },
                    },
                    error: {
                      style: {
                        background: mode === 'dark' ? '#d32f2f' : '#dc2626',
                        border: mode === 'dark' ? '1px solid rgba(244, 67, 54, 0.7)' : '1px solid #ef4444',
                        color: '#ffffff',
                      },
                    },
                  }}
                  containerStyle={{
                    bottom: '20px',
                    right: '20px',
                  }}
                />
                      <VercelAnalytics />
                      <ErrorBoundary>
                        <GoogleOneTapWrapper />
                      </ErrorBoundary>
                    </div>
                </Router>
              </SubscriptionProvider>
            </ErrorBoundary>
          </AuthProvider>
        </ErrorBoundary>
      </HelmetProvider>
      </ThemeProvider>
    );
  };

  // Main App component
  function App() {
    return (
      <QueryClientProvider client={queryClient}>
        <ThemeModeProvider>
          <AppContent />
        </ThemeModeProvider>
      </QueryClientProvider>
    );
  }

export default App; 